import { useId, useState } from 'react'

import TextInput from '@components/atoms/form/TextInput'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

import { VIOLET, embedTheme, parseHex } from '@utils/embeds/theme'

const t = scopedTranslation('embeds.editor.colour')

const SWATCHES = ['#0F766E', '#15803D', VIOLET, '#1D4ED8', '#B91C1C', '#C2410C', '#111322']

type ColorFieldProps = {
  label: string
  value: string
  defaultColor: string
  onChange: (color: string) => void
}

export function ColorField({ label, value, defaultColor, onChange }: ColorFieldProps) {
  const id = useId()
  const [draft, setDraft] = useState<{ text: string; blurred: boolean } | null>(null)
  const swatches = Array.from(new Set([defaultColor, ...SWATCHES]))
  const invalid = Boolean(draft?.blurred && !parseHex(draft.text))

  return (
    <fieldset className="tw:m-0 tw:flex tw:flex-col tw:gap-3 tw:border-0 tw:p-0">
      <legend className="tw:mb-2 tw:pb-1 tw:text-sm tw:font-medium tw:text-gray-700">{label}</legend>
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        {swatches.map((swatch) => {
          const selected = swatch.toUpperCase() === value.toUpperCase()
          return (
            <label key={swatch} className="tw:m-0 tw:cursor-pointer">
              <input
                type="radio"
                name={`${id}-swatch`}
                value={swatch}
                checked={selected}
                onChange={() => {
                  setDraft(null)
                  onChange(swatch)
                }}
                aria-label={t('use', { colour: swatch })}
                className="tw:peer tw:sr-only"
              />
              <span
                aria-hidden
                className="tw:flex tw:h-7 tw:w-7 tw:items-center tw:justify-center tw:rounded-full tw:ring-1 tw:ring-gray-cool-300 tw:peer-checked:ring-2 tw:peer-checked:ring-gray-cool-900 tw:peer-checked:ring-offset-2 tw:peer-focus-visible:outline tw:peer-focus-visible:outline-2 tw:peer-focus-visible:outline-offset-4 tw:peer-focus-visible:outline-primary-400"
                style={{ backgroundColor: swatch, color: embedTheme(swatch).brandText }}
              >
                {selected && <Icon type="check" className="tw:h-4 tw:w-4" />}
              </span>
            </label>
          )
        })}
      </div>
      <div className="tw:w-40">
        <TextInput
          name={`${id}-hex`}
          label={t('hex')}
          value={draft?.text ?? value}
          leadingContent={
            <input
              type="color"
              aria-label={t('pick')}
              value={value}
              onChange={(event) => {
                setDraft(null)
                onChange(parseHex(event.target.value) ?? value)
              }}
              className="tw:h-6 tw:w-6 tw:shrink-0 tw:cursor-pointer tw:appearance-none tw:rounded tw:border-0 tw:bg-transparent tw:p-0 tw:ring-1 tw:ring-gray-cool-300 tw:focus-visible:outline tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-primary-400 tw:[&::-moz-color-swatch]:rounded tw:[&::-moz-color-swatch]:border-0 tw:[&::-webkit-color-swatch-wrapper]:p-0 tw:[&::-webkit-color-swatch]:rounded tw:[&::-webkit-color-swatch]:border-0"
            />
          }
          errors={invalid ? { [`${id}-hex`]: { type: 'pattern', message: t('invalid') } } : {}}
          onBlur={() =>
            setDraft((current) => (current && !parseHex(current.text) ? { ...current, blurred: true } : null))
          }
          onChange={(event) => {
            const text = event.target.value
            setDraft((current) => ({ text, blurred: current?.blurred ?? false }))
            const color = parseHex(text)
            if (color) onChange(color)
          }}
        />
      </div>
    </fieldset>
  )
}
